import {
    TaskAttribute,
    InputDefinition,
    InputType,
    RatingsAttributesData,
    AttributesData,
    ValueDataType,
    VisibilityConditionType,
} from 'Types/Tasks.types'
import styles from './RatingsAttribute.module.less'
import Form, { FormInstance, Rule } from 'antd/lib/form'
import { AttributeValueType, AttributeValueTypeDefinitions } from 'Types/Attribute.types'
import { AttributesFactory } from 'Core/AttributesFactory'
import Tooltip from 'antd/lib/tooltip'
import InfoCircleFilled from '@ant-design/icons/lib/icons/InfoCircleFilled'
import Space from 'antd/lib/space'
import { RefObject, createRef, useCallback, useEffect, useMemo, useRef } from 'react'
import { isEmpty } from 'lodash'
import {
    getDefaultAttributeValue,
    performLogicalOperation,
    validateOperator,
} from 'Utils/attributes.utils'
import { DEFAULT_ERROR_MESSAGE } from 'Constants/constants'
import { useMessage } from 'shared/hooks/use-message'

export type RatingsAttributeBodyPropsType = {
    ratingsAttributes: TaskAttribute[]
    globalAttributes: TaskAttribute[]
    globalValues?: AttributesData
    onUpdateAttribute: (name: string, value: AttributeValueType, responseIndex: string) => void
    responseIds: string[]
    attributesOnUI: { [key: string]: 1 }
    values?: RatingsAttributesData[]
    getClickedResponseId?: () => string
    getHoveredResponseId?: () => string
    setForm: (form: FormInstance) => void
    isReadOnly: () => boolean
}

export const RatingsAttributeBody = (props: RatingsAttributeBodyPropsType) => {
    const [form] = Form.useForm()
    const toUpdateRef = useRef<
        Record<string, { name: string; value: AttributeValueType; responseId: string }>
    >({})
    const [messageApi] = useMessage()

    useEffect(() => {
        props.setForm(form)
    }, [form, props])

    useEffect(() => {
        Object.entries(toUpdateRef.current).forEach(([, data]) => {
            form.setFieldValue([data.name + '__' + data.responseId], data.value)
            props.onUpdateAttribute(data.name, data.value as AttributeValueType, data.responseId)
        })

        toUpdateRef.current = {}
    }, [form, props])

    const refs = props.responseIds.reduce(
        (acc, value: string) => {
            acc[value] = createRef()
            return acc
        },
        {} as { [key: string]: RefObject<HTMLDivElement> }
    )

    const initialValues = useMemo(() => {
        return props.responseIds.reduce(
            (acc, responseId) => {
                props.ratingsAttributes.forEach(attribute => {
                    const foundValue = props.values?.find(value => value.response_id === responseId)
                        ?.ratings[attribute.name]?.data.value
                    acc[attribute.name + '__' + responseId] =
                        foundValue ??
                        getDefaultAttributeValue(attribute.value_definition.value_type)
                })
                return acc
            },
            {} as Record<string, AttributeValueType>
        )
    }, [props.responseIds, props.ratingsAttributes, props.values])

    const findDependsOnAttributeAndValue = useCallback(
        (
            visibilityCondition: VisibilityConditionType,
            responseId: string
        ): {
            dependsOnAttribute: TaskAttribute | undefined
            dependsOnAttributeValue: AttributeValueType
            dependsOnAttributeUIName: string
        } => {
            const dependsOnResponseAttribute = props.ratingsAttributes.find(
                (attribute: TaskAttribute) =>
                    attribute.version_id === visibilityCondition.depends_on_attribute_version_id
            )

            let dependsOnGlobalAttribute
            if (!dependsOnResponseAttribute) {
                dependsOnGlobalAttribute = props.globalAttributes.find(
                    (attribute: TaskAttribute) =>
                        attribute.version_id === visibilityCondition.depends_on_attribute_version_id
                )
            }

            const dependsOnAttribute = dependsOnResponseAttribute ?? dependsOnGlobalAttribute
            let dependsOnAttributeValue
            let dependsOnAttributeUIName = ''

            if (dependsOnResponseAttribute) {
                dependsOnAttributeValue = props.values?.find(
                    value => value.response_id === responseId
                )?.ratings[dependsOnResponseAttribute.name]?.data.value
                dependsOnAttributeUIName = dependsOnResponseAttribute.name + '__' + responseId
            } else if (dependsOnGlobalAttribute) {
                dependsOnAttributeValue =
                    props.globalValues?.[dependsOnGlobalAttribute.name]?.data.value
                dependsOnAttributeUIName = dependsOnGlobalAttribute.name
            }

            return { dependsOnAttribute, dependsOnAttributeValue, dependsOnAttributeUIName }
        },
        [props.globalAttributes, props.globalValues, props.values, props.ratingsAttributes]
    )

    const clearAttributeValueIfNecessary = (
        attribute: TaskAttribute,
        responseId: string,
        currentAttributeValue: ValueDataType<keyof AttributeValueTypeDefinitions> | undefined
    ) => {
        if (currentAttributeValue !== undefined) {
            toUpdateRef.current[attribute.name + '__' + responseId] = {
                name: attribute.name,
                value: undefined,
                responseId,
            }
        }
    }

    const showAttribute = useCallback(
        (currentAttribute: TaskAttribute, responseId: string): boolean => {
            const visibilityCondition = currentAttribute.visibility_condition
            if (!visibilityCondition) return true
            const { dependsOnAttribute, dependsOnAttributeValue, dependsOnAttributeUIName } =
                findDependsOnAttributeAndValue(visibilityCondition, responseId)

            if (!dependsOnAttribute) {
                messageApi.destroy()
                messageApi.error(DEFAULT_ERROR_MESSAGE)
                throw new Error('Conditional attribute exist without depends on attribute')
            }

            const currentAttributeValue = props.values?.find(
                value => value.response_id === responseId
            )?.ratings[currentAttribute.name]

            if (!props.attributesOnUI[dependsOnAttributeUIName]) {
                clearAttributeValueIfNecessary(currentAttribute, responseId, currentAttributeValue)
                return false
            }
            validateOperator(dependsOnAttribute, visibilityCondition.operator)

            const isVisible = performLogicalOperation(
                (dependsOnAttribute.input_definition as InputDefinition).input_type,
                visibilityCondition.operator,
                dependsOnAttributeValue,
                visibilityCondition.value
            )
            if (!isVisible) {
                clearAttributeValueIfNecessary(currentAttribute, responseId, currentAttributeValue)
            }
            return isVisible
        },
        [props.values, props.attributesOnUI, findDependsOnAttributeAndValue, messageApi]
    )

    const RatingsAttribute = useCallback(
        (responseId: string, responseIndex: number, isReadOnly: boolean) => {
            return props.ratingsAttributes.map((attribute: TaskAttribute) => {
                if (!showAttribute(attribute, responseId)) {
                    delete props.attributesOnUI[attribute.name + '__' + responseId]
                    return null
                }
                props.attributesOnUI[attribute.name + '__' + responseId] = 1
                const attributeProps = {
                    value:
                        props.values?.find(value => value.response_id === responseId)?.ratings[
                            attribute.name
                        ]?.data.value ??
                        getDefaultAttributeValue(attribute.value_definition.value_type),
                    config: attribute,
                    className: styles['attribute'],
                    onChange: (value: AttributeValueType, name: string) => {
                        props.onUpdateAttribute(name, value, responseId)
                    },
                    readonly: isReadOnly,
                }
                const rules: Rule[] = [
                    {
                        required: !attribute.input_definition?.is_optional,
                        message: 'Please fill out this field.',
                    },
                ]

                if (attribute.input_definition?.input_type === InputType.INT_SCALE) {
                    rules.push({
                        type: 'number',
                        min: attribute.input_definition.min,
                        max: attribute.input_definition.max,
                        message: `Value should be between ${attribute.input_definition.min} and ${attribute.input_definition.max}`,
                    })
                }

                if (attribute.input_definition?.input_type === InputType.INT) {
                    rules.push({
                        validator: (_, value) => {
                            if (value && !Number.isInteger(value)) {
                                return Promise.reject(new Error('Only integer values are allowed'))
                            }

                            return Promise.resolve()
                        },
                        required: true,
                    })
                }

                return (
                    <Form.Item
                        name={attribute.name + '__' + responseId}
                        rules={rules}
                        key={attribute.label + '__' + responseId}
                        label={
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '13px',
                                }}
                            >
                                <div>{attribute.label}</div>
                                <Space style={{ marginLeft: 3, zIndex: 3 }}>
                                    {!attribute.input_definition?.is_optional && (
                                        <span className={styles['label-error']}>*</span>
                                    )}
                                    {!!attribute.description && (
                                        <Tooltip title={attribute.description || ''}>
                                            <InfoCircleFilled />
                                        </Tooltip>
                                    )}
                                </Space>
                            </div>
                        }
                        labelCol={{ style: { order: 0 } }}
                        colon={false}
                    >
                        <AttributesFactory
                            key={attribute.attribute_id + '__' + responseIndex}
                            {...attributeProps}
                        />
                    </Form.Item>
                )
            })
        },
        [props, showAttribute]
    )

    useEffect(() => {
        if (isEmpty(props.getClickedResponseId?.())) {
            return
        }
        refs?.[props.getClickedResponseId?.() ?? '']?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'start',
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.getClickedResponseId?.()])

    return (
        <div className={styles['rating-attributes-body-container']}>
            <Form form={form} layout="vertical" initialValues={initialValues}>
                {props.responseIds.map((responseId: string, responseIndex: number) => (
                    <div
                        className={
                            styles['attributes-container'] +
                            ' ' +
                            (responseId === props.getHoveredResponseId?.() ? styles['hovered'] : '')
                        }
                        key={responseId}
                        ref={refs[responseId]}
                    >
                        <div className={styles['attributes-response-header']}>
                            <span>Response</span>
                            <span>{responseIndex + 1}</span>
                        </div>
                        {RatingsAttribute(responseId, responseIndex, props.isReadOnly())}
                    </div>
                ))}
            </Form>
        </div>
    )
}
